import { useCallback, useEffect, useId, useRef, useState } from "react"
import { Loader2, PanelRightOpen, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { useGetClientUserApi, type ClientUserDetail } from "@/hooks/api/crm"
import { normalizeApiError } from "@/lib/apiErrors"
import type { Workspace } from "@/types"
import { ThreadCrmEditor } from "./ThreadCrmEditor"

export interface ThreadCrmNotesProps {
  organizationId: string
  workspace: Workspace
  clientUserId?: string | null
  onOpenChange?: (open: boolean) => void
}

// Remount the request scope even when A → B → A happens before A completes.
export const ThreadCrmNotes = (props: ThreadCrmNotesProps) => (
  <ThreadCrmNotesContent
    key={`${props.organizationId}:${props.workspace.id}:${props.clientUserId}:${props.workspace.maskEndUserIdentity}`}
    {...props}
  />
)

const ThreadCrmNotesContent = ({
  organizationId,
  workspace,
  clientUserId,
  onOpenChange,
}: ThreadCrmNotesProps) => {
  const { t } = useTranslation()
  const notesId = useId()
  const titleId = useId()
  const notesRef = useRef<HTMLDivElement>(null)
  const editRef = useRef<HTMLButtonElement>(null)
  const requestRef = useRef<AbortController | null>(null)
  const [open, setOpen] = useState(false)
  const openRef = useRef(open)
  openRef.current = open
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [result, setResult] = useState<
    | { status: "loading" }
    | { status: "ready"; user: ClientUserDetail }
    | { status: "error"; error: unknown }
  >({ status: "loading" })
  const { fetchFn: getUser } = useGetClientUserApi(
    organizationId,
    workspace.id,
    clientUserId || ""
  )
  const canLoad = Boolean(clientUserId && !workspace.maskEndUserIdentity)
  const load = useCallback(async () => {
    // Keep the editing snapshot stable, including during focus/visibility refreshes.
    if (!canLoad || openRef.current) return
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setResult({ status: "loading" })
    try {
      const response = await getUser({
        signal: controller.signal,
        cache: "no-store",
      })
      if (controller.signal.aborted) return
      if (!response?.data) throw new Error("CRM customer response unavailable")
      setResult({ status: "ready", user: response.data })
    } catch (error) {
      if (!controller.signal.aborted) setResult({ status: "error", error })
    }
  }, [canLoad, getUser])

  useEffect(() => {
    void load()
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") void load()
    }
    window.addEventListener("focus", refreshOnReturn)
    document.addEventListener("visibilitychange", refreshOnReturn)
    return () => {
      requestRef.current?.abort()
      window.removeEventListener("focus", refreshOnReturn)
      document.removeEventListener("visibilitychange", refreshOnReturn)
    }
  }, [load])

  const user = result.status === "ready" ? result.user : undefined
  const visibility = user?.provider_notes_visibility
  const notes = visibility === "visible" ? user?.provider_notes : null
  useEffect(() => {
    const element = notesRef.current
    if (!element) return
    const measure = () => setHasOverflow(element.scrollHeight > 60)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [notes, expanded])

  const apiError =
    result.status === "error" ? normalizeApiError(result.error) : null
  const isMasked =
    workspace.maskEndUserIdentity || visibility === "identity_masked"
  const isDenied =
    visibility === "permission_denied" || apiError?.kind === "permission_denied"
  const isMissing = !clientUserId || apiError?.status === 404
  const canEdit = !isMasked && !isMissing && visibility === "visible"
  useEffect(() => {
    onOpenChange?.(open && canEdit)
    return () => onOpenChange?.(false)
  }, [open, canEdit, onOpenChange])
  const notice = isMasked
    ? t("historyList.thread.crmNotes.masked")
    : isMissing
      ? t("historyList.thread.crmNotes.noCustomer")
      : isDenied
        ? t("historyList.thread.crmNotes.permissionDenied")
        : result.status === "error" || (user && visibility !== "visible")
          ? t("historyList.thread.crmNotes.loadFailed")
          : !notes?.trim()
            ? t("historyList.thread.crmNotes.empty")
            : null
  const isLoading = canLoad && result.status === "loading"

  return (
    <>
      <section
        aria-labelledby={titleId}
        className={cn(
          "shrink-0 border-b border-border/70 bg-muted/20 px-4 py-2 xl:px-5",
          open && canEdit && "hidden"
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 items-center gap-2 text-xs">
            <h3 id={titleId} className="shrink-0 font-medium">
              {t("historyList.thread.crmNotes.title")}
            </h3>
            {!isMasked && !isMissing && user?.display_name && (
              <span
                className="truncate text-muted-foreground"
                title={user.display_name}
              >
                {user.display_name}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canEdit && (
              <Button
                ref={editRef}
                type="button"
                variant="link"
                size="sm"
                className="h-7 px-0 text-xs"
                aria-expanded={open}
                onClick={() => setOpen(true)}
              >
                {t("historyList.thread.crmNotes.edit")}
                <PanelRightOpen className="ml-1 h-3 w-3" aria-hidden="true" />
              </Button>
            )}
            {canLoad && !isMissing && !isDenied && !isMasked && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={isLoading}
                aria-label={t("historyList.thread.crmNotes.refresh")}
                onClick={() => void load()}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div
            role="status"
            className="mt-1 flex items-center text-xs text-muted-foreground"
          >
            <Loader2
              className="mr-1.5 h-3 w-3 animate-spin"
              aria-hidden="true"
            />
            {t("historyList.thread.crmNotes.loading")}
          </div>
        ) : notice ? (
          <div
            className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
            role="status"
          >
            {notice}
            {result.status === "error" &&
              !isDenied &&
              !isMissing &&
              !isMasked && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => void load()}
                >
                  {t("historyList.thread.crmNotes.retry")}
                </Button>
              )}
          </div>
        ) : (
          <>
            <div
              id={notesId}
              ref={notesRef}
              tabIndex={expanded ? 0 : undefined}
              aria-label={t("historyList.thread.crmNotes.title")}
              className={cn(
                "mt-1 whitespace-pre-wrap break-words text-sm leading-5 [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                expanded
                  ? "max-h-[min(20vh,100px)] overflow-y-auto sm:max-h-[min(24vh,160px)]"
                  : "max-h-[60px] overflow-hidden"
              )}
            >
              {notes}
            </div>
            {hasOverflow && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="mt-1 h-auto p-0 text-xs"
                aria-expanded={expanded}
                aria-controls={notesId}
                onClick={() => setExpanded((value) => !value)}
              >
                {t(
                  expanded
                    ? "historyList.thread.crmNotes.collapse"
                    : "historyList.thread.crmNotes.expand"
                )}
              </Button>
            )}
          </>
        )}
      </section>
      {open && canEdit && user && (
        <aside
          aria-label={t("historyList.thread.crmNotes.customerDetails")}
          className="absolute inset-y-0 right-0 z-30 w-full border-l bg-background @min-[760px]/crm:w-80"
        >
          <ThreadCrmEditor
            organizationId={organizationId}
            workspaceId={workspace.id}
            user={user}
            onSaved={(user) => setResult({ status: "ready", user })}
            onClose={() => {
              setOpen(false)
              requestAnimationFrame(() => editRef.current?.focus())
            }}
          />
        </aside>
      )}
    </>
  )
}
